import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import logo from '../../logo.png';
import { FileText, Download, Calendar, BarChart3 } from 'lucide-react';

export default function Reportes() {
  const navigate = useNavigate();
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tipoReporte, setTipoReporte] = useState('general');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  useEffect(() => {
    fetchDatos();
  }, []);

  const fetchDatos = async () => {
    try {
      const response = await api.get('/dashboard');
      setDatos(response.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const generarReporteGeneral = () => {
    if (!datos) return;

    let csv = 'REPORTE GENERAL DOMINIUM\n';
    csv += `Fecha de generación: ${new Date().toLocaleString('es-MX')}\n\n`;

    csv += 'MÉTRICAS GENERALES\n';
    csv += '-------------------\n';
    csv += `Total de Capturas,${datos.metricas.total_promovidos}\n`;
    csv += `Promovidos Activos,${datos.metricas.total_activos}\n`;
    csv += `Duplicidades,${datos.metricas.total_duplicidades}\n`;
    csv += `Estructuras,${datos.metricas.total_estructuras}\n`;
    csv += `Banco de Talento,${datos.metricas.total_talento}\n`;
    csv += `Usuarios del Sistema,${datos.metricas.total_usuarios}\n\n`;

    csv += 'PROMOVIDOS POR SECCIÓN\n';
    csv += '---------------------\n';
    csv += 'Sección,Promovidos Activos,Meta de Votos\n';
    datos.graficas.por_seccion.forEach(s => {
      csv += `${s.numero_seccion},${s.promovidos_activos},${s.meta_votos}\n`;
    });

    csv += '\nNIVEL DE APOYO\n';
    csv += '-------------\n';
    csv += 'Nivel,Total\n';
    datos.graficas.por_nivel_apoyo.forEach(n => {
      csv += `${n.nivel_apoyo},${n.total}\n`;
    });

    csv += '\nCAPTURAS RECIENTES\n';
    csv += '-----------------\n';
    csv += 'Persona,Teléfono,Sección,Estructura,Capturado por,Estatus\n';
    datos.capturas_recientes.forEach(c => {
      csv += `"${c.persona}","${c.telefono_principal}","${c.numero_seccion}","${c.nombre_estructura}","${c.capturado_por}","${c.estatus_captura}"\n`;
    });

    descargarCSV(csv, `reporte_general_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const generarReportePromovidos = () => {
    if (!datos) return;

    let csv = 'REPORTE DE PROMOVIDOS\n';
    csv += `Fecha de generación: ${new Date().toLocaleString('es-MX')}\n\n`;
    csv += 'Persona,Teléfono,Sección,Estructura,Capturado por,Estatus\n';
    
    datos.capturas_recientes.forEach(c => {
      csv += `"${c.persona}","${c.telefono_principal}","${c.numero_seccion}","${c.nombre_estructura}","${c.capturado_por}","${c.estatus_captura}"\n`;
    });

    descargarCSV(csv, `reporte_promovidos_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const generarReporteSeccional = () => {
    if (!datos) return;

    let csv = 'REPORTE POR SECCIÓN\n';
    csv += `Fecha de generación: ${new Date().toLocaleString('es-MX')}\n\n`;
    csv += 'Sección,Promovidos Activos,Meta de Votos,Avance (%)\n';
    
    datos.graficas.por_seccion.forEach(s => {
      const avance = s.meta_votos > 0 ? ((s.promovidos_activos / s.meta_votos) * 100).toFixed(2) : 0;
      csv += `${s.numero_seccion},${s.promovidos_activos},${s.meta_votos},${avance}\n`;
    });

    descargarCSV(csv, `reporte_seccional_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const generarReporteApoyo = () => {
    if (!datos) return;

    let csv = 'REPORTE DE NIVEL DE APOYO\n';
    csv += `Fecha de generación: ${new Date().toLocaleString('es-MX')}\n\n`;
    csv += 'Nivel de Apoyo,Total,Porcentaje (%)\n';
    
    const totalApoyo = datos.graficas.por_nivel_apoyo.reduce((sum, n) => sum + n.total, 0);
    datos.graficas.por_nivel_apoyo.forEach(n => {
      const porcentaje = totalApoyo > 0 ? ((n.total / totalApoyo) * 100).toFixed(2) : 0;
      csv += `${n.nivel_apoyo},${n.total},${porcentaje}\n`;
    });

    descargarCSV(csv, `reporte_apoyo_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const descargarCSV = (csv, nombreArchivo) => {
    const link = document.createElement('a');
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    link.download = nombreArchivo;
    link.click();
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
        style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
        <img src={logo} alt="DOMINIUM" className="h-16" />
        <button onClick={() => navigate('/dashboard')}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
          ← Volver al Dashboard
        </button>
      </header>

      <main className="p-6 max-w-6xl mx-auto">

        {/* Título */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <BarChart3 className="text-blue-600" size={28} />
            Centro de Reportes
          </h2>
          <p className="text-gray-500 text-sm">Genera y descarga reportes de operación en CSV</p>
        </div>

        {/* Reportes disponibles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Reporte General */}
          <div className="bg-white rounded-xl shadow-sm p-6 border-t-4 border-blue-400">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-lg text-gray-800">Reporte General</h3>
                <p className="text-sm text-gray-500">Métricas completas del sistema</p>
              </div>
              <FileText className="text-blue-600" size={24} />
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Incluye: métricas, secciones, nivel de apoyo, capturas recientes
            </p>
            <button onClick={generarReporteGeneral} disabled={loading}
              className="w-full text-white font-semibold py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
              <Download size={16} />
              Descargar CSV
            </button>
          </div>

          {/* Reporte Promovidos */}
          <div className="bg-white rounded-xl shadow-sm p-6 border-t-4 border-green-400">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-lg text-gray-800">Reporte de Promovidos</h3>
                <p className="text-sm text-gray-500">Listado de todos los promovidos</p>
              </div>
              <FileText className="text-green-600" size={24} />
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Incluye: nombre, teléfono, sección, estructura, capturador, estatus
            </p>
            <button onClick={generarReportePromovidos} disabled={loading}
              className="w-full text-white font-semibold py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
              <Download size={16} />
              Descargar CSV
            </button>
          </div>

          {/* Reporte Seccional */}
          <div className="bg-white rounded-xl shadow-sm p-6 border-t-4 border-purple-400">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-lg text-gray-800">Reporte Seccional</h3>
                <p className="text-sm text-gray-500">Avance por sección electoral</p>
              </div>
              <BarChart3 className="text-purple-600" size={24} />
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Incluye: sección, promovidos, meta, porcentaje de avance
            </p>
            <button onClick={generarReporteSeccional} disabled={loading}
              className="w-full text-white font-semibold py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
              <Download size={16} />
              Descargar CSV
            </button>
          </div>

          {/* Reporte Apoyo */}
          <div className="bg-white rounded-xl shadow-sm p-6 border-t-4 border-orange-400">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-lg text-gray-800">Reporte de Nivel de Apoyo</h3>
                <p className="text-sm text-gray-500">Desglose de apoyo político</p>
              </div>
              <BarChart3 className="text-orange-600" size={24} />
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Incluye: nivel de apoyo, totales, porcentajes
            </p>
            <button onClick={generarReporteApoyo} disabled={loading}
              className="w-full text-white font-semibold py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
              <Download size={16} />
              Descargar CSV
            </button>
          </div>

        </div>

        {/* Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mt-6">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">📊 Nota:</span> Todos los reportes se descargan en formato CSV (compatible con Excel, Google Sheets, etc.). Los datos incluyen la fecha y hora de generación para auditoría.
          </p>
        </div>

        <p className="text-center text-gray-400 text-xs mt-8">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>

      </main>
    </div>
  );
}